import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["firstName", "lastName", "photoUrl", "liveStreamingState", "referralType"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import useTestId from '@ori/testid-generator';
import { Pbs as PbsWaveIcon } from '@ori-ui/icons';
import { Avatar, Stack, styled, Typography, useTheme } from '@ori-ui/mui';
import { LiveStreamingStateWrap } from "../common/LiveStreamingStateWrap";
import { Trans } from "../Trans";
import { useTranslation } from "../../hooks";
import { LiveStreamingStates, TooltipTestIds, TranslationKeys, ReferralType } from "../../models";
import { formatUserName } from "../../utils";

/**
 * Avatar size. Value is in pixels
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var AVATAR_SIZE = 50;

/**
 * Max width of tooltip component.
 */
var ROOT_MAX_WIDTH = 230;

/**
 * How large fallback icon representing avatar should be
 */
var fallBackAvatarIconSize = 27;

/**
 * Tooltip Arrow width and height
 */
var ARROW_SIZE = 30; // px

/**
 * Tooltip arrow
 */
var ARROW_OFFSET = {
  x: 7,
  // px
  y: 0 // px
};

var Root = styled('div')(function (_ref) {
  var theme = _ref.theme,
    onClick = _ref.onClick;
  return _objectSpread({
    maxWidth: ROOT_MAX_WIDTH,
    width: '100vw',
    position: 'relative',
    boxShadow: theme.shadows[4],
    borderRadius: theme.shape.largeComponentRadius,
    display: 'flex'
  }, onClick && {
    cursor: 'pointer'
  });
});
var ContentWrap = styled('div')(function (_ref2) {
  var theme = _ref2.theme;
  return {
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(3),
    width: '100%',
    borderRadius: theme.shape.largeComponentRadius,
    zIndex: 1,
    position: 'relative'
  };
});
var TitleWrap = styled(Stack)(function (_ref3) {
  var theme = _ref3.theme;
  return {
    marginBottom: theme.spacing(1.5),
    justifyContent: 'space-between',
    alignItems: 'center'
  };
});
var Title = styled(Typography)({
  fontWeight: 'bold',
  lineHeight: '22px'
});
var StyledAvatar = styled(Avatar)(function (_ref4) {
  var theme = _ref4.theme;
  return {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.grey[900]
  };
});
var TooltipArrow = styled('div')(function (_ref5) {
  var theme = _ref5.theme;
  return {
    overflow: 'hidden',
    position: 'absolute',
    zIndex: 0,
    width: ARROW_SIZE,
    height: ARROW_SIZE,
    top: ARROW_OFFSET.y,
    left: ARROW_OFFSET.x,
    boxShadow: theme.shadows[4],
    borderRadius: theme.shape.mediumComponentRadius,
    backgroundColor: theme.palette.common.white,
    transform: 'rotate(33deg) skewY(25deg)'
  };
});
var getContentTranslationKey = function getContentTranslationKey(type) {
  switch (type) {
    case ReferralType.PBS:
      return TranslationKeys.ReferralMessageTooltip;
    case ReferralType.ECREF:
      return TranslationKeys.InviteMessageTooltip;
    default:
      throw new Error('Unknown referral tooltip type.');
  }
};
export var Tooltip = function Tooltip(_ref6) {
  var firstName = _ref6.firstName,
    lastName = _ref6.lastName,
    photoUrl = _ref6.photoUrl,
    liveStreamingState = _ref6.liveStreamingState,
    referralType = _ref6.referralType,
    restProps = _objectWithoutProperties(_ref6, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var theme = useTheme();
  var _useTestId = useTestId(),
    getTestIdProps = _useTestId.getTestIdProps;
  var fullName = formatUserName(firstName, lastName);
  return /*#__PURE__*/_jsxs(Root, _objectSpread(_objectSpread(_objectSpread({}, getTestIdProps(TooltipTestIds.ROOT)), restProps), {}, {
    children: [/*#__PURE__*/_jsx(TooltipArrow, {}), /*#__PURE__*/_jsxs(ContentWrap, {
      children: [/*#__PURE__*/_jsxs(TitleWrap, {
        direction: "row",
        children: [/*#__PURE__*/_jsx(Title, _objectSpread(_objectSpread({
          variant: "subtitle1",
          color: theme.palette.grey[900]
        }, getTestIdProps(TooltipTestIds.TITLE)), {}, {
          children: t(TranslationKeys.Greetings)
        })), /*#__PURE__*/_jsx(LiveStreamingStateWrap, {
          streamingActivity: liveStreamingState !== null && liveStreamingState !== void 0 ? liveStreamingState : LiveStreamingStates.UNKNOWN,
          children: /*#__PURE__*/_jsx(StyledAvatar, _objectSpread(_objectSpread({
            src: photoUrl !== null && photoUrl !== void 0 ? photoUrl : '',
            alt: t(TranslationKeys.SponsorPhoto)
          }, getTestIdProps(TooltipTestIds.AVATAR)), {}, {
            children: /*#__PURE__*/_jsx(PbsWaveIcon, _objectSpread({
              sx: {
                width: fallBackAvatarIconSize,
                height: fallBackAvatarIconSize
              },
              viewBox: "2.8 4 16.5 16"
            }, getTestIdProps(TooltipTestIds.FALLBACK_AVATAR)))
          }))
        })]
      }), /*#__PURE__*/_jsx(Typography, _objectSpread(_objectSpread({
        variant: "body2",
        component: "p",
        color: theme.palette.grey[900]
      }, getTestIdProps(TooltipTestIds.TEXT)), {}, {
        children: /*#__PURE__*/_jsx(Trans, {
          i18nKey: getContentTranslationKey(referralType),
          values: {
            fullName: fullName
          },
          components: {
            emphasis: /*#__PURE__*/_jsx("strong", _objectSpread({}, getTestIdProps(TooltipTestIds.USER_NAME)))
          }
        })
      }))]
    })]
  }));
};